import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API_URL, formatDate } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Plus, CheckSquare, Clock, Calendar, MessageSquare, Send, Check, X, 
  Edit, Trash2, Play, ChevronDown, ChevronRight, CornerDownRight, Filter, RotateCcw, Users, FileText
} from 'lucide-react';
import { toast } from 'sonner';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { CreateTaskPageDialog, CreateSubtaskPageDialog, EditTaskPageDialog, TaskDetailPageDialog } from '../components/tasks/TaskDialogs';

const taskStatuses = [
  { id: 'not_started', label: 'Not Started', color: 'bg-muted text-muted-foreground' },
  { id: 'assigned', label: 'Assigned', color: 'bg-cyan-500/20 text-cyan-600' },
  { id: 'in_progress', label: 'In Progress', color: 'bg-blue-500/20 text-blue-600' },
  { id: 'on_hold', label: 'On Hold', color: 'bg-yellow-500/20 text-yellow-600' },
  { id: 'under_review', label: 'Under Review', color: 'bg-purple-500/20 text-purple-600' },
  { id: 'overdue', label: 'Overdue', color: 'bg-red-500/20 text-red-600' },
  { id: 'completed', label: 'Completed', color: 'bg-green-500/20 text-green-600' }
];

// Statuses users can manually select (overdue is auto-assigned)
const selectableStatuses = taskStatuses.filter(s => s.id !== 'overdue');

const priorities = [
  { id: 'low', label: 'Low', color: 'bg-slate-500/20 text-slate-600' },
  { id: 'medium', label: 'Medium', color: 'bg-blue-500/20 text-blue-600' },
  { id: 'high', label: 'High', color: 'bg-orange-500/20 text-orange-600' },
  { id: 'urgent', label: 'Overdue', color: 'bg-red-500/20 text-red-600' }
];

const formatTime = (minutes) => {
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
};

export const TasksPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isTaskDialog, setIsTaskDialog] = useState(false);
  const [isTaskDetailDialog, setIsTaskDetailDialog] = useState(false);
  const [isSubtaskDialog, setIsSubtaskDialog] = useState(false);
  const [isEditDialog, setIsEditDialog] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [expandedTasks, setExpandedTasks] = useState({});
  const [newComment, setNewComment] = useState('');
  const [editingComment, setEditingComment] = useState(null);
  const [editCommentText, setEditCommentText] = useState('');
  
  // Filters - arrays for multi-select (initialized from query params if present)
  const [filterPriority, setFilterPriority] = useState([]);
  const [filterAssignee, setFilterAssignee] = useState(() => {
    const assignee = searchParams.get('assignee');
    return assignee ? [assignee] : [];
  });
  const [filterProject, setFilterProject] = useState([]);
  const [filterStatus, setFilterStatus] = useState(() => {
    const statuses = searchParams.get('statuses');
    return statuses ? statuses.split(',') : ['assigned', 'in_progress', 'on_hold', 'under_review', 'overdue'];
  });
  
  const [taskForm, setTaskForm] = useState({
    project_id: '',
    is_internal: false,
    title: '',
    description: '',
    priority: 'medium',
    assigned_to: '',
    start_date: '',
    end_date: '',
    reviewer_id: '',
    parent_task_id: '',  // For nesting under existing task
    subtasks_to_create: [],  // Auto-create subtasks
    initial_comment: ''  // Comment to add at creation
  });
  
  const [subtaskForm, setSubtaskForm] = useState({
    title: '',
    description: '',
    priority: 'medium',
    assigned_to: '',
    initial_comment: ''
  });
  
  const [newSubtaskTitle, setNewSubtaskTitle] = useState('');
  
  const [editForm, setEditForm] = useState({});
  
  // Bulk selection (admin only)
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const [isBulkEditDialog, setIsBulkEditDialog] = useState(false);
  const [bulkEditForm, setBulkEditForm] = useState({ assigned_to: '', reviewer_id: '' });

  // Multi-select filter toggle helper
  const toggleFilter = (setter, value) => {
    setter(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);
  };

  const fetchData = useCallback(async () => {
    try {
      const [tasksRes, projectsRes, teamRes] = await Promise.all([
        axios.get(`${API_URL}/tasks?include_internal=true&limit=5000`, { withCredentials: true }),
        axios.get(`${API_URL}/projects`, { withCredentials: true }),
        axios.get(`${API_URL}/team`, { withCredentials: true })
      ]);
      setTasks(tasksRes.data);
      setProjects(projectsRes.data);
      setTeamMembers(teamRes.data);
    } catch (error) {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle query params (new, open)
  useEffect(() => {
    if (searchParams.get('new') === 'true') {
      setIsTaskDialog(true);
    }
    const openTaskId = searchParams.get('open');
    if (openTaskId && tasks.length > 0) {
      const taskToOpen = tasks.find(t => t.task_id === openTaskId);
      if (taskToOpen) {
        openTaskDetail(taskToOpen);
      } else {
        // Task not in loaded batch — fetch it directly
        axios.get(`${API_URL}/tasks/${openTaskId}`, { withCredentials: true })
          .then(res => {
            if (res.data) {
              openTaskDetail(res.data);
            }
          })
          .catch(() => {});
      }
    }
  }, [searchParams, tasks]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter tasks
  const filteredTasks = tasks.filter(task => {
    if (task.parent_task_id) return false; // Only show parent tasks, subtasks shown nested
    if (filterPriority.length > 0 && !filterPriority.includes(task.priority)) return false;
    if (filterAssignee.length > 0) {
      const isUnassigned = !task.assigned_to;
      const matchesAssignee = filterAssignee.includes(task.assigned_to);
      const matchesUnassigned = filterAssignee.includes('__unassigned__') && isUnassigned;
      if (!matchesAssignee && !matchesUnassigned) return false;
    }
    if (filterProject.length > 0 && !filterProject.includes(task.project_id)) return false;
    if (filterStatus.length > 0 && !filterStatus.includes(task.status)) return false;
    return true;
  });

  // Sort: Overdue first, then by priority (high → medium → low)
  const priorityOrder = { urgent: 0, high: 1, medium: 2, low: 3 };
  const sortedFilteredTasks = [...filteredTasks].sort((a, b) => {
    const now = new Date();
    const aOverdue = a.end_date && new Date(a.end_date) < now && a.status !== 'completed' ? 1 : 0;
    const bOverdue = b.end_date && new Date(b.end_date) < now && b.status !== 'completed' ? 1 : 0;
    if (bOverdue !== aOverdue) return bOverdue - aOverdue; // Overdue first
    return (priorityOrder[a.priority] || 3) - (priorityOrder[b.priority] || 3);
  });

  // Group tasks by assignee
  const tasksByAssignee = useMemo(() => {
    const groups = {};
    sortedFilteredTasks.forEach(task => {
      const assigneeId = task.assigned_to || '__unassigned__';
      const assigneeName = task.assigned_to
        ? (teamMembers.find(m => m.user_id === task.assigned_to)?.name || 'Unknown')
        : 'Unassigned';
      if (!groups[assigneeId]) {
        groups[assigneeId] = { name: assigneeName, tasks: [] };
      }
      groups[assigneeId].tasks.push(task);
    });
    return Object.entries(groups).sort(([a], [b]) => {
      if (a === '__unassigned__') return 1;
      if (b === '__unassigned__') return -1;
      return groups[a].name.localeCompare(groups[b].name);
    });
  }, [sortedFilteredTasks, teamMembers]);

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      // Create main task
      const taskRes = await axios.post(`${API_URL}/tasks`, {
        project_id: taskForm.is_internal ? null : taskForm.project_id,
        is_internal: taskForm.is_internal,
        title: taskForm.title,
        description: taskForm.description,
        priority: taskForm.priority,
        assigned_to: taskForm.assigned_to || null,
        start_date: taskForm.start_date || null,
        end_date: taskForm.end_date || null,
        reviewer_id: taskForm.reviewer_id || null,
        parent_task_id: taskForm.parent_task_id || null
      }, { withCredentials: true });
      
      // Create subtasks if any
      if (taskForm.subtasks_to_create.length > 0) {
        const subtaskIds = [];
        for (const subtask of taskForm.subtasks_to_create) {
          const stRes = await axios.post(`${API_URL}/tasks`, {
            project_id: taskForm.is_internal ? null : taskForm.project_id,
            is_internal: taskForm.is_internal,
            parent_task_id: taskRes.data.task_id,
            title: subtask.title,
            description: subtask.description || '',
            priority: taskForm.priority,
            assigned_to: taskForm.assigned_to || null
          }, { withCredentials: true });
          subtaskIds.push(stRes.data.task_id);
        }
        // Update parent task with subtask IDs
        await axios.patch(`${API_URL}/tasks/${taskRes.data.task_id}`, {
          subtasks: subtaskIds
        }, { withCredentials: true });
      }
      
      // If nested under another task, update parent's subtasks array
      if (taskForm.parent_task_id) {
        const parentTask = tasks.find(t => t.task_id === taskForm.parent_task_id);
        if (parentTask) {
          await axios.patch(`${API_URL}/tasks/${taskForm.parent_task_id}`, {
            subtasks: [...(parentTask.subtasks || []), taskRes.data.task_id]
          }, { withCredentials: true });
        }
      }
      
      toast.success(taskForm.subtasks_to_create.length > 0 
        ? `Task created with ${taskForm.subtasks_to_create.length} subtasks` 
        : 'Task created');
      setIsTaskDialog(false);
      setTaskForm({ 
        project_id: '', is_internal: false, title: '', description: '', priority: 'medium', 
        assigned_to: '', start_date: '', end_date: '', reviewer_id: '',
        parent_task_id: '', subtasks_to_create: []
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to create task');
    }
  };

  const handleCreateSubtask = async (e) => {
    e.preventDefault();
    if (!selectedTask) return;
    try {
      const res = await axios.post(`${API_URL}/tasks`, {
        project_id: selectedTask.project_id,
        parent_task_id: selectedTask.task_id,
        ...subtaskForm
      }, { withCredentials: true });
      
      await axios.patch(`${API_URL}/tasks/${selectedTask.task_id}`, {
        subtasks: [...(selectedTask.subtasks || []), res.data.task_id]
      }, { withCredentials: true });
      
      toast.success('Subtask created');
      setIsSubtaskDialog(false);
      setSubtaskForm({ title: '', description: '', priority: 'medium', assigned_to: '' });
      fetchData();
      refreshSelectedTask();
    } catch (error) {
      toast.error('Failed to create subtask');
    }
  };

  const handleUpdateTask = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(`${API_URL}/tasks/${selectedTask.task_id}`, editForm, { withCredentials: true });
      toast.success('Task updated');
      setIsEditDialog(false);
      fetchData();
      refreshSelectedTask();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Delete this task and all its subtasks?')) return;
    try {
      await axios.delete(`${API_URL}/tasks/${taskId}`, { withCredentials: true });
      toast.success('Task deleted');
      setIsTaskDetailDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await axios.patch(`${API_URL}/tasks/${taskId}`, { status: newStatus }, { withCredentials: true });
      fetchData();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const refreshSelectedTask = async () => {
    if (!selectedTask) return;
    try {
      const res = await axios.get(`${API_URL}/tasks/${selectedTask.task_id}`, { withCredentials: true });
      setSelectedTask(res.data);
    } catch (err) {
      console.warn('Failed to refresh selected task:', err);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    try {
      await axios.post(
        `${API_URL}/tasks/${selectedTask.task_id}/add-comment`,
        null,
        { params: { comment: newComment }, withCredentials: true }
      );
      toast.success('Comment added');
      setNewComment('');
      refreshSelectedTask();
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const handleEditComment = async (commentId) => {
    if (!editCommentText.trim()) return;
    try {
      await axios.patch(
        `${API_URL}/tasks/${selectedTask.task_id}/comments/${commentId}`,
        null,
        { params: { new_comment: editCommentText }, withCredentials: true }
      );
      toast.success('Comment updated');
      setEditingComment(null);
      setEditCommentText('');
      refreshSelectedTask();
    } catch (error) {
      toast.error('Failed to update comment');
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;
    try {
      await axios.delete(
        `${API_URL}/tasks/${selectedTask.task_id}/comments/${commentId}`,
        { withCredentials: true }
      );
      toast.success('Comment deleted');
      refreshSelectedTask();
    } catch (error) {
      toast.error('Failed to delete comment');
    }
  };

  const handleSendForReview = async (taskId, reviewerId) => {
    try {
      await axios.post(
        `${API_URL}/tasks/${taskId}/send-for-review`,
        null,
        { params: { reviewer_id: reviewerId }, withCredentials: true }
      );
      toast.success('Task sent for review');
      setIsTaskDetailDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to send for review');
    }
  };

  const handleApproveReview = async (taskId) => {
    try {
      await axios.post(`${API_URL}/tasks/${taskId}/approve-review`, null, { withCredentials: true });
      toast.success('Task approved');
      setIsTaskDetailDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to approve');
    }
  };

  const handleReturnToOwner = async (taskId) => {
    const notes = window.prompt('Enter notes for the task owner (optional):');
    try {
      await axios.post(
        `${API_URL}/tasks/${taskId}/return-to-owner`,
        null,
        { params: { notes }, withCredentials: true }
      );
      toast.success('Task returned to owner');
      setIsTaskDetailDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to return task');
    }
  };

  const handleStartTimer = async (taskId) => {
    try {
      await axios.post(`${API_URL}/timer/start`, null, {
        params: { task_id: taskId },
        withCredentials: true
      });
      toast.success('Timer started');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start timer');
    }
  };

  const openTaskDetail = (task) => {
    setSelectedTask(task);
    setEditForm(task);
    setIsTaskDetailDialog(true);
  };

  // Bulk task actions (admin only)
  const toggleTaskSelection = (taskId) => {
    setSelectedTaskIds(prev => prev.includes(taskId) ? prev.filter(id => id !== taskId) : [...prev, taskId]);
  };

  const toggleAllTasks = () => {
    if (selectedTaskIds.length === sortedFilteredTasks.length) {
      setSelectedTaskIds([]);
    } else {
      setSelectedTaskIds(sortedFilteredTasks.map(t => t.task_id));
    }
  };

  const handleBulkDeleteTasks = async () => {
    if (!window.confirm(`Delete ${selectedTaskIds.length} selected task(s)? This cannot be undone.`)) return;
    try {
      await Promise.all(selectedTaskIds.map(id => axios.delete(`${API_URL}/tasks/${id}`, { withCredentials: true })));
      toast.success(`${selectedTaskIds.length} task(s) deleted`);
      setSelectedTaskIds([]);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete some tasks');
    }
  };

  const handleBulkEditTasks = async () => {
    const updates = {};
    if (bulkEditForm.assigned_to) updates.assigned_to = bulkEditForm.assigned_to;
    if (bulkEditForm.reviewer_id) updates.reviewer_id = bulkEditForm.reviewer_id;
    if (Object.keys(updates).length === 0) {
      toast.error('Please select at least one field to update');
      return;
    }
    try {
      await Promise.all(selectedTaskIds.map(id => axios.patch(`${API_URL}/tasks/${id}`, updates, { withCredentials: true })));
      toast.success(`${selectedTaskIds.length} task(s) updated`);
      setSelectedTaskIds([]);
      setIsBulkEditDialog(false);
      setBulkEditForm({ assigned_to: '', reviewer_id: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to update some tasks');
    }
  };

  const getStatusBadge = (status) => {
    const statusInfo = taskStatuses.find(s => s.id === status) || taskStatuses[0];
    return <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusInfo.color}`}>{statusInfo.label}</span>;
  };

  const getPriorityBadge = (priority) => {
    const priorityInfo = priorities.find(p => p.id === priority) || priorities[1];
    return <span className={`px-2 py-1 text-xs font-medium rounded-full ${priorityInfo.color}`}>{priorityInfo.label}</span>;
  };

  const isReviewer = selectedTask?.reviewer_id === user?.user_id;

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  }

  return (
    <div data-testid="tasks-page" className="pt-12">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-4xl font-heading font-bold tracking-tight mb-2">Tasks</h1>
          <p className="text-base text-muted-foreground">Manage all tasks across projects</p>
        </div>
        <Button onClick={() => setIsTaskDialog(true)} data-testid="create-task-btn">
          <Plus className="mr-2 h-4 w-4" />
          New Task
        </Button>
      </div>

      {/* Filters */}
      <Card className="p-4 mb-6">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Filters:</span>
          </div>
          
          {/* Project multi-select */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-9">
                Projects {filterProject.length > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-primary/20 rounded-full">{filterProject.length}</span>}
                <ChevronDown className="ml-1 h-3 w-3" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-2 max-h-64 overflow-y-auto" align="start">
              {projects.map((p) => (
                <label key={p.project_id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted cursor-pointer text-sm">
                  <Checkbox checked={filterProject.includes(p.project_id)} onCheckedChange={() => toggleFilter(setFilterProject, p.project_id)} />
                  <span className="truncate">{p.name}</span>
                </label>
              ))}
              {filterProject.length > 0 && (
                <Button variant="ghost" size="sm" className="w-full mt-1 text-xs" onClick={() => setFilterProject([])}>Clear</Button>
              )}
            </PopoverContent>
          </Popover>

          {/* Priority multi-select */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-9">
                Priority {filterPriority.length > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-primary/20 rounded-full">{filterPriority.length}</span>}
                <ChevronDown className="ml-1 h-3 w-3" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-48 p-2" align="start">
              {priorities.map((p) => (
                <label key={p.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted cursor-pointer text-sm">
                  <Checkbox checked={filterPriority.includes(p.id)} onCheckedChange={() => toggleFilter(setFilterPriority, p.id)} />
                  {p.label}
                </label>
              ))}
              {filterPriority.length > 0 && (
                <Button variant="ghost" size="sm" className="w-full mt-1 text-xs" onClick={() => setFilterPriority([])}>Clear</Button>
              )}
            </PopoverContent>
          </Popover>

          {/* Assignee multi-select with Unassigned option */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-9">
                Assignee {filterAssignee.length > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-primary/20 rounded-full">{filterAssignee.length}</span>}
                <ChevronDown className="ml-1 h-3 w-3" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-2 max-h-64 overflow-y-auto" align="start">
              <label className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted cursor-pointer text-sm font-medium border-b mb-1 pb-1.5">
                <Checkbox checked={filterAssignee.includes('__unassigned__')} onCheckedChange={() => toggleFilter(setFilterAssignee, '__unassigned__')} />
                Unassigned
              </label>
              {teamMembers.map((m) => (
                <label key={m.user_id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted cursor-pointer text-sm">
                  <Checkbox checked={filterAssignee.includes(m.user_id)} onCheckedChange={() => toggleFilter(setFilterAssignee, m.user_id)} />
                  <span className="truncate">{m.name}</span>
                </label>
              ))}
              {filterAssignee.length > 0 && (
                <Button variant="ghost" size="sm" className="w-full mt-1 text-xs" onClick={() => setFilterAssignee([])}>Clear</Button>
              )}
            </PopoverContent>
          </Popover>

          {/* Status multi-select */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-9" data-testid="task-status-filter">
                Status {filterStatus.length > 0 && filterStatus.length < taskStatuses.length && <span className="ml-1 px-1.5 py-0.5 text-xs bg-primary/20 rounded-full">{filterStatus.length}</span>}
                <ChevronDown className="ml-1 h-3 w-3" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-52 p-2" align="start">
              <div className="flex gap-1 mb-2">
                <Button variant="ghost" size="sm" className="flex-1 text-xs h-7" onClick={() => setFilterStatus(taskStatuses.map(s => s.id))}>All</Button>
                <Button variant="ghost" size="sm" className="flex-1 text-xs h-7" onClick={() => setFilterStatus([])}>None</Button>
              </div>
              {taskStatuses.map((s) => (
                <label key={s.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted cursor-pointer text-sm">
                  <Checkbox checked={filterStatus.includes(s.id)} onCheckedChange={() => toggleFilter(setFilterStatus, s.id)} />
                  {s.label}
                </label>
              ))}
            </PopoverContent>
          </Popover>

          {(filterProject.length > 0 || filterPriority.length > 0 || filterAssignee.length > 0 || filterStatus.length !== 5) && (
            <Button variant="ghost" size="sm" onClick={() => {
              setFilterProject([]);
              setFilterPriority([]);
              setFilterAssignee([]);
              setFilterStatus(['assigned', 'in_progress', 'on_hold', 'under_review', 'overdue']);
            }}>
              Reset Filters
            </Button>
          )}
        </div>
      </Card>

      {/* Tasks List - Grouped by Assignee */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-heading font-semibold">
            Tasks ({filteredTasks.length})
          </h3>
          {user?.role === 'admin' && sortedFilteredTasks.length > 0 && (
            <div className="flex items-center gap-2">
              <Checkbox
                checked={selectedTaskIds.length === sortedFilteredTasks.length && sortedFilteredTasks.length > 0}
                onCheckedChange={toggleAllTasks}
                data-testid="tasks-select-all"
              />
              <span className="text-xs text-muted-foreground">Select All</span>
            </div>
          )}
        </div>

        {/* Bulk Actions Bar */}
        {user?.role === 'admin' && selectedTaskIds.length > 0 && (
          <div className="flex items-center gap-3 p-3 bg-muted rounded-lg mb-4" data-testid="tasks-bulk-actions-bar">
            <span className="text-sm font-medium">{selectedTaskIds.length} selected</span>
            <Button size="sm" variant="outline" onClick={() => setIsBulkEditDialog(true)} data-testid="tasks-bulk-edit-btn">
              <Edit className="mr-1 h-3 w-3" /> Edit
            </Button>
            <Button size="sm" variant="destructive" onClick={handleBulkDeleteTasks} data-testid="tasks-bulk-delete-btn">
              <Trash2 className="mr-1 h-3 w-3" /> Delete
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSelectedTaskIds([])}>Clear</Button>
          </div>
        )}

        {filteredTasks.length === 0 ? (
          <div className="text-center py-8">
            <CheckSquare className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">No tasks match your filters</p>
          </div>
        ) : (
          <div className="space-y-6">
            {tasksByAssignee.map(([assigneeId, group]) => (
              <div key={assigneeId}>
                <div className="flex items-center gap-2 mb-3 pb-2 border-b">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <h4 className="font-medium text-sm">{group.name}</h4>
                  <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{group.tasks.length} tasks</span>
                </div>
                <div className="space-y-3 pl-2">
            {group.tasks.map((task) => {
              const subtasks = tasks.filter(t => t.parent_task_id === task.task_id);
              const isExpanded = expandedTasks[task.task_id];
              const project = projects.find(p => p.project_id === task.project_id);
              
              return (
                <div key={task.task_id}>
                  <div 
                    className="p-4 rounded-lg border bg-card hover:bg-muted/50 cursor-pointer transition-colors"
                    onClick={() => openTaskDetail(task)}
                    data-testid={`task-item-${task.task_id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 flex items-start gap-3">
                        {user?.role === 'admin' && (
                          <div className="pt-1" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={selectedTaskIds.includes(task.task_id)}
                              onCheckedChange={() => toggleTaskSelection(task.task_id)}
                            />
                          </div>
                        )}
                        <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {subtasks.length > 0 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedTasks(prev => ({ ...prev, [task.task_id]: !prev[task.task_id] }));
                              }}
                              className="p-1 hover:bg-muted rounded"
                            >
                              {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            </button>
                          )}
                          <h4 className="font-medium">{task.title}</h4>
                          {task.is_internal && (
                            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-500/20 text-amber-600">Internal</span>
                          )}
                          {task.is_fee_linked && (
                            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-emerald-500/20 text-emerald-700" data-testid={`fee-linked-badge-${task.task_id}`}>Fee Structure</span>
                          )}
                          {getStatusBadge(task.status)}
                          {getPriorityBadge(task.priority)}
                          {task.end_date && new Date(task.end_date) < new Date() && task.status !== 'completed' && (
                            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-500/20 text-red-600 animate-pulse">Overdue</span>
                          )}
                          {subtasks.length > 0 && (
                            <span className="text-xs text-muted-foreground">({subtasks.length} subtasks)</span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          {project && (
                            <span className="px-2 py-0.5 bg-muted rounded">{project.name}</span>
                          )}
                          {!project && !task.is_internal && (
                            <span className="px-2 py-0.5 bg-muted rounded text-muted-foreground">No Project</span>
                          )}
                          {task.assigned_to && (
                            <span>Assigned: {teamMembers.find(m => m.user_id === task.assigned_to)?.name || 'Unknown'}</span>
                          )}
                          {task.end_date && (
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              Due: {formatDate(task.end_date)}
                            </span>
                          )}
                          {task.comments?.length > 0 && (
                            <span className="flex items-center gap-1">
                              <MessageSquare className="h-3 w-3" />
                              {task.comments.length}
                            </span>
                          )}
                          {task.total_tracked_time > 0 && (
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatTime(task.total_tracked_time)}
                            </span>
                          )}
                        </div>
                      </div>
                      </div>
                      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
                        <Button size="sm" variant="ghost" onClick={() => handleStartTimer(task.task_id)} title="Start Timer">
                          <Play className="h-4 w-4" />
                        </Button>
                        <Select value={task.status} onValueChange={(value) => handleStatusChange(task.task_id, value)}>
                          <SelectTrigger className="w-36">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {selectableStatuses.map((status) => (
                              <SelectItem key={status.id} value={status.id}>{status.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                  
                  {/* Subtasks */}
                  {isExpanded && subtasks.length > 0 && (
                    <div className="ml-8 mt-2 space-y-2">
                      {subtasks.map((subtask) => (
                        <div 
                          key={subtask.task_id}
                          className="p-3 rounded-lg border bg-muted/30 hover:bg-muted/50 cursor-pointer transition-colors flex items-center gap-3"
                          onClick={() => openTaskDetail(subtask)}
                        >
                          <CornerDownRight className="h-4 w-4 text-muted-foreground" />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm">{subtask.title}</span>
                              {getStatusBadge(subtask.status)}
                            </div>
                          </div>
                          <div onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
                          <Select 
                            value={subtask.status} 
                            onValueChange={(value) => handleStatusChange(subtask.task_id, value)}
                          >
                            <SelectTrigger className="w-28 h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {selectableStatuses.map((status) => (
                                <SelectItem key={status.id} value={status.id}>{status.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Extracted Dialogs */}
      <CreateTaskPageDialog
        open={isTaskDialog} onOpenChange={setIsTaskDialog}
        form={taskForm} setForm={setTaskForm} onSubmit={handleCreateTask}
        projects={projects} tasks={tasks} teamMembers={teamMembers}
        newSubtaskTitle={newSubtaskTitle} setNewSubtaskTitle={setNewSubtaskTitle}
      />
      <CreateSubtaskPageDialog
        open={isSubtaskDialog} onOpenChange={setIsSubtaskDialog}
        form={subtaskForm} setForm={setSubtaskForm} onSubmit={handleCreateSubtask}
        parentTitle={selectedTask?.title} teamMembers={teamMembers}
      />
      <EditTaskPageDialog
        open={isEditDialog} onOpenChange={setIsEditDialog}
        form={editForm} setForm={setEditForm} onSubmit={handleUpdateTask}
        teamMembers={teamMembers}
      />
      <TaskDetailPageDialog
        open={isTaskDetailDialog} onOpenChange={setIsTaskDetailDialog}
        task={selectedTask} user={user} teamMembers={teamMembers} projects={projects}
        newComment={newComment} setNewComment={setNewComment}
        editingComment={editingComment} setEditingComment={setEditingComment}
        editCommentText={editCommentText} setEditCommentText={setEditCommentText}
        onAddComment={handleAddComment} onEditComment={handleEditComment} onDeleteComment={handleDeleteComment}
        onStatusChange={handleStatusChange}
        onEdit={(task) => { setEditForm(task); setIsEditDialog(true); }}
        onDelete={handleDeleteTask} onStartTimer={handleStartTimer}
        onSendForReview={handleSendForReview} onApproveReview={handleApproveReview} onReturnToOwner={handleReturnToOwner}
        onCreateSubtask={() => setIsSubtaskDialog(true)}
      />

      {/* Bulk Edit Dialog (admin only) */}
      <Dialog open={isBulkEditDialog} onOpenChange={setIsBulkEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Bulk Edit {selectedTaskIds.length} Task(s)</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Assign To</label>
              <Select value={bulkEditForm.assigned_to || '__none__'} onValueChange={(v) => setBulkEditForm(prev => ({ ...prev, assigned_to: v === '__none__' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="No change" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No change</SelectItem>
                  {teamMembers.map((m) => (<SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Reviewer</label>
              <Select value={bulkEditForm.reviewer_id || '__none__'} onValueChange={(v) => setBulkEditForm(prev => ({ ...prev, reviewer_id: v === '__none__' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="No change" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No change</SelectItem>
                  {teamMembers.filter(m => ['admin', 'manager', 'team_lead'].includes(m.role)).map((m) => (
                    <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsBulkEditDialog(false)}>Cancel</Button>
              <Button onClick={handleBulkEditTasks} data-testid="tasks-bulk-edit-submit">Apply Changes</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

